import type { SubscriptionSetupModel, SubscriptionSetupStep } from './index';
import { createInitialState, subscriptionSetupReducer } from './reducer';
import type { SubscriptionSetupAction, SubscriptionSetupState } from './reducer';
import type { Instant } from '@js-joda/core';
import { computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { MutationObserver } from '@tanstack/query-core';
import type { DefaultError } from '@tanstack/query-core';
import type {
    CreateSubscriptionRequest,
    CreateSubscriptionResponse,
    SetupIntentResponse,
    Subscription,
    SubscriptionPlan,
} from '@thinkalpha/access-ws-client/contracts/subscription.js';
import type { SecurityPackage } from '@thinkalpha/access-ws-client/contracts/user.js';
import type { ProTraderStatus } from '@thinkalpha/common/schemas/auth.js';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import { inject, injectable } from 'src/features/ioc';
import { getProTraderStatusFromToken } from 'src/features/oauth2';
import { connectReducerDevtools } from 'src/lib/devtools';
import type { ReducerDevtoolsConnection } from 'src/lib/devtools';
import { showInviteCodesForProfessionalTraders } from 'src/lib/featureFlags/flags/showInviteCodesForProfessionalTraders';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { SubscriptionPlanCatalogModel } from 'src/models/SubscriptionPlanCatalogModel';
import {
    isActiveSubscriptionStatus,
    isMainSubscription,
    isPlanSelectableForTraderStatus,
    statusToIsProfessionalTrader,
} from 'src/models/SubscriptionPlanCatalogModel/rules';
import {
    getCancelSubscriptionMutation,
    getCreateSubscriptionMutation,
    getMySubscriptionsQuery,
    getSetupIntentMutation,
    invalidateMySubscriptionsQuery,
} from 'src/queries/subscriptions';
import { getUserSecurityPackageQuery } from 'src/queries/user';
import type { SessionService } from 'src/services/SessionService';
import type { ReactBindings } from 'src/types/bindings';
import type { z } from 'zod';

@injectable()
export class SubscriptionSetupModelImpl implements SubscriptionSetupModel {
    readonly #disposableStack = new DisposableStack();
    #log: Logger;

    #state = signal<SubscriptionSetupState>(createInitialState());

    #mySubscriptionsQuery: QueryObserverModel<z.infer<typeof Subscription>[], Error, z.infer<typeof Subscription>[]>;
    #securityPackageQuery: QueryObserverModel<
        z.infer<typeof SecurityPackage>,
        DefaultError,
        z.infer<typeof SecurityPackage>
    >;
    #planCatalog!: SubscriptionPlanCatalogModel;

    #createSubscriptionMutation: MutationObserver<
        z.infer<typeof CreateSubscriptionResponse>,
        Error,
        z.infer<typeof CreateSubscriptionRequest>
    >;

    #cancelSubscriptionMutation: MutationObserver<
        z.infer<typeof Subscription>,
        Error,
        { id: string; immediately?: boolean }
    >;

    #setupIntentMutation: MutationObserver<z.infer<typeof SetupIntentResponse>, Error, void>;

    #devtools: ReducerDevtoolsConnection<SubscriptionSetupState, SubscriptionSetupAction> | undefined;
    #showInviteCodesForProfessionalTraders: ReadonlySignal<boolean> = signal(false);

    #dispatch(action: SubscriptionSetupAction): void {
        this.#state.value = subscriptionSetupReducer(this.#state.value, action);
        this.#devtools?.send(action, this.#state.peek());
    }

    readonly currentStep: ReadonlySignal<SubscriptionSetupStep> = computed(() => this.#state.value.currentStep);
    readonly selectedPlanId: ReadonlySignal<string | null> = computed(() => this.#state.value.selectedPlanId);
    readonly inviteCode: ReadonlySignal<string> = computed(() => this.#planCatalog?.inviteCode.value ?? '');
    readonly proTraderStatus: ReadonlySignal<ProTraderStatus> = computed(() =>
        getProTraderStatusFromToken(
            this.store.signal.value.auth?.masqAccessToken?.token ??
                this.sessionService.accessToken.value ??
                this.store.signal.value.auth?.accessToken?.token,
        ),
    );
    readonly isProfessionalTrader: ReadonlySignal<boolean> = computed(() =>
        statusToIsProfessionalTrader(this.proTraderStatus.value),
    );
    readonly showInviteCodeEntry: ReadonlySignal<boolean> = computed(
        () => !this.isProfessionalTrader.value || this.#showInviteCodesForProfessionalTraders.value,
    );
    readonly error: ReadonlySignal<string | null> = computed(() => this.#state.value.error);
    readonly awaitingActivation: ReadonlySignal<boolean> = computed(() => this.#state.value.awaitingActivation);
    readonly cancelingSubscription: ReadonlySignal<boolean> = computed(() => this.#state.value.cancelingSubscription);

    readonly #hasActiveMainSubscription: ReadonlySignal<boolean> = computed(
        () =>
            this.#mySubscriptionsQuery?.signal.value.data?.some(
                (subscription) => isMainSubscription(subscription) && isActiveSubscriptionStatus(subscription.status),
            ) ?? false,
    );

    readonly plans: ReadonlySignal<z.infer<typeof SubscriptionPlan>[]> = computed(
        () => this.#planCatalog?.plans.value ?? [],
    );

    readonly availablePlans: ReadonlySignal<z.infer<typeof SubscriptionPlan>[]> = computed(
        () => this.#planCatalog?.availablePlans.value ?? [],
    );

    readonly plansLoading: ReadonlySignal<boolean> = computed(() => this.#planCatalog?.plansLoading.value ?? false);

    readonly submitting: ReadonlySignal<boolean> = computed(
        () =>
            (this.#state.value.createSubscriptionResult?.isPending ?? false) ||
            (this.#state.value.setupIntentResult?.isPending ?? false) ||
            this.#state.value.cancelingSubscription ||
            false,
    );

    readonly clientSecret: ReadonlySignal<string | null> = computed(() => {
        const setupIntentSecret = this.#state.value.setupIntentResult?.data?.clientSecret;
        if (setupIntentSecret) return setupIntentSecret;
        return this.#state.value.createSubscriptionResult?.data?.clientSecret ?? null;
    });

    readonly selectedPlan: ReadonlySignal<z.infer<typeof SubscriptionPlan> | null> = computed(() => {
        const planId = this.#state.value.selectedPlanId;
        if (!planId) return null;
        return this.availablePlans.value.find((p) => p.id === planId) ?? null;
    });

    readonly isInviteCodeValid: ReadonlySignal<boolean | null> = computed(
        () => this.#planCatalog?.isInviteCodeValid.value ?? null,
    );

    constructor(
        @inject('QueryObserverModelFactory')
        private queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('QueryClient')
        private queryClient: ReactBindings['QueryClient'],
        @inject('Logger') private logger: ReactBindings['Logger'],
        @inject('Store') private store: ReactBindings['Store'],
        @inject('FeatureFlagService') private featureFlagService: ReactBindings['FeatureFlagService'],
        @inject('CurrentUserModel') private currentUserModel: ReactBindings['CurrentUserModel'],
        @inject('SessionService') private sessionService: SessionService,
    ) {
        this.#showInviteCodesForProfessionalTraders = this.featureFlagService.getSignal(
            showInviteCodesForProfessionalTraders,
        );
        this.#log = this.logger.getSubLogger({ name: 'models:SubscriptionSetupModel' });
        this.#securityPackageQuery = this.queryObserverModelFactory<
            z.infer<typeof SecurityPackage>,
            DefaultError,
            z.infer<typeof SecurityPackage>
        >(computed(() => getUserSecurityPackageQuery('me', this.#state.value.knownLastFailedAt)));
        this.#disposableStack.use(this.#securityPackageQuery);

        // Query for user's subscription history (includes canceled subscriptions for trial eligibility)
        this.#mySubscriptionsQuery = this.queryObserverModelFactory<
            z.infer<typeof Subscription>[],
            Error,
            z.infer<typeof Subscription>[]
        >(getMySubscriptionsQuery());
        this.#disposableStack.use(this.#mySubscriptionsQuery);

        this.#planCatalog = new SubscriptionPlanCatalogModel({
            queryObserverModelFactory: this.queryObserverModelFactory,
            isProfessionalTrader: this.isProfessionalTrader,
            showInviteCodeEntry: this.showInviteCodeEntry,
            includePlan: (plan) => !this.#hasActiveMainSubscription.value || plan.isAddOn === true,
        });
        this.#disposableStack.use(this.#planCatalog);

        // Watch for subscription activation after payment is confirmed
        this.#disposableStack.defer(
            effect(() => {
                if (!this.#state.value.awaitingActivation) return;

                const createdSubscriptionId =
                    this.#state.value.createSubscriptionResult?.data?.subscription.id ??
                    this.#state.value.existingPendingSubscriptionId;
                if (!createdSubscriptionId) return;

                const secPkg = this.#securityPackageQuery?.signal.value.data;
                if (!secPkg) return;

                const subscription = secPkg.subscriptions.find((s) => s.id === createdSubscriptionId);
                if (!subscription) return;

                if (subscription.status === 'active' || subscription.status === 'trialing') {
                    if (TRACE) {
                        this.#log.trace('Subscription activated:', {
                            subscriptionId: createdSubscriptionId,
                            subscriptionStatus: subscription.status,
                        });
                    }
                    this.#dispatch({ type: 'SUBSCRIPTION_ACTIVATED' });
                    this.currentUserModel.notifyEntitlementsChanged();
                } else if (subscription.status === 'pending_payment_method') {
                    // Check whether lastFailedAt has changed from our baseline. If not, the webhook
                    // hasn't been processed yet — keep polling. If it has changed, payment failed.
                    const currentLastFailedAt = subscription.lastFailedAt ?? null;
                    const knownLastFailedAt = this.#state.value.knownLastFailedAt;
                    const lastFailedAtChanged =
                        currentLastFailedAt === null
                            ? knownLastFailedAt !== null
                            : knownLastFailedAt === null || !currentLastFailedAt.equals(knownLastFailedAt);
                    if (lastFailedAtChanged) {
                        if (TRACE) {
                            this.#log.trace('Payment activation failed (lastFailedAt updated):', {
                                subscriptionId: createdSubscriptionId,
                                lastFailedAt: currentLastFailedAt?.toString(),
                            });
                        }
                        this.#dispatch({
                            type: 'PAYMENT_FAILED',
                            error: 'Payment activation failed. Please check your payment method and try again.',
                        });
                    }
                } else if (subscription.status !== 'pending_activation') {
                    // Unexpected state
                    if (TRACE) {
                        this.#log.trace('Subscription entered unexpected state:', {
                            subscriptionId: createdSubscriptionId,
                            subscriptionStatus: subscription.status,
                        });
                    }
                    this.#dispatch({
                        type: 'PAYMENT_FAILED',
                        error: 'Payment activation failed. Please check your payment method and try again.',
                    });
                }
            }),
        );

        this.#disposableStack.defer(
            effect(() => {
                const inviteCodePlan = this.#planCatalog.inviteCodePlan.value;
                if (inviteCodePlan && this.#state.value.selectedPlanId !== inviteCodePlan.id) {
                    this.#dispatch({ type: 'SELECT_PLAN', planId: inviteCodePlan.id });
                }
            }),
        );

        // Auto-select default plan or follow replacement when the selected plan is superseded
        this.#disposableStack.defer(
            effect(() => {
                const plans = this.availablePlans.value;
                const selectedPlanId = this.#state.value.selectedPlanId;

                if (plans.length === 0) {
                    if (selectedPlanId) {
                        this.#dispatch({ type: 'CLEAR_SELECTED_PLAN' });
                    }
                    return;
                }

                if (selectedPlanId) {
                    if (plans.some((p) => p.id === selectedPlanId)) return;

                    // Selected plan was filtered out — find the plan that replaces it
                    const replacement = plans.find(
                        (p) =>
                            p.replacesPlanIds?.includes(selectedPlanId) &&
                            isPlanSelectableForTraderStatus(p, this.isProfessionalTrader.value),
                    );
                    if (replacement) {
                        this.#dispatch({ type: 'SELECT_PLAN', planId: replacement.id });
                        return;
                    }
                }

                // No selection (or selected plan gone with no replacement) — pick the default
                const defaultPlan = plans.find(
                    (p) => p.default && isPlanSelectableForTraderStatus(p, this.isProfessionalTrader.value),
                );
                if (defaultPlan) {
                    this.#dispatch({ type: 'SELECT_PLAN', planId: defaultPlan.id });
                } else if (selectedPlanId) {
                    this.#dispatch({ type: 'CLEAR_SELECTED_PLAN' });
                }
            }),
        );

        this.#createSubscriptionMutation = new MutationObserver(this.queryClient, getCreateSubscriptionMutation());
        this.#disposableStack.defer(
            this.#createSubscriptionMutation.subscribe((result) => {
                this.#dispatch({ type: 'CREATE_SUBSCRIPTION_RESULT_UPDATED', result });
            }),
        );

        this.#cancelSubscriptionMutation = new MutationObserver(this.queryClient, getCancelSubscriptionMutation());

        this.#setupIntentMutation = new MutationObserver(this.queryClient, getSetupIntentMutation());
        this.#disposableStack.defer(
            this.#setupIntentMutation.subscribe((result) => {
                this.#dispatch({ type: 'SETUP_INTENT_RESULT_UPDATED', result });
            }),
        );

        this.#devtools = connectReducerDevtools({ name: 'SubscriptionSetup' });
        if (this.#devtools) {
            this.#disposableStack.use(this.#devtools);
            this.#devtools.init(this.#state.value);
        }
    }

    setInviteCode(inviteCode: string): void {
        this.#dispatch({ type: 'SET_INVITE_CODE', inviteCode });
        this.#planCatalog.setInviteCode(inviteCode);
    }

    selectPlan(planId: string): void {
        this.#dispatch({ type: 'SELECT_PLAN', planId });
    }

    async fetchPlans(): Promise<void> {
        this.#dispatch({ type: 'CLEAR_ERROR' });

        const inviteCode = this.showInviteCodeEntry.value ? this.inviteCode.value.trim() : '';
        const result = await this.#planCatalog.fetchPlans();
        if (inviteCode) {
            if (result.isError) {
                const err = result.error as any;
                if (err?.response?.status === 404) {
                    this.#dispatch({
                        type: 'FETCH_PLANS_ERROR',
                        error: 'Invalid invite code. Please check and try again.',
                    });
                } else {
                    this.#dispatch({
                        type: 'FETCH_PLANS_ERROR',
                        error: err?.message ?? 'Failed to load plans',
                    });
                }
            }
        } else {
            if (result.isError) {
                this.#dispatch({
                    type: 'FETCH_PLANS_ERROR',
                    error: (result.error as any)?.message ?? 'Failed to load plans',
                });
            }
        }
    }

    async submitSubscription(): Promise<void> {
        this.#dispatch({ type: 'SUBMIT_START' });

        try {
            const inviteCode = this.showInviteCodeEntry.value ? this.inviteCode.value.trim() : '';
            const selectedPlanId = this.#state.value.selectedPlanId;
            const selectedPlan = this.selectedPlan.value;
            if (
                !selectedPlanId ||
                !selectedPlan ||
                !isPlanSelectableForTraderStatus(selectedPlan, this.isProfessionalTrader.value)
            ) {
                throw new Error('A valid subscription plan must be selected');
            }

            // Cancel any existing pending subscription before creating a new one.
            // This handles the case where a user switches between valid plans.
            const existingSubscriptionId =
                this.#state.value.createSubscriptionResult?.data?.subscription.id ??
                this.#state.value.existingPendingSubscriptionId;
            if (existingSubscriptionId) {
                await this.cancelPendingSubscription();
            }

            const request = {
                planId: selectedPlanId,
                inviteCode: inviteCode || undefined,
                isProfessionalTrader: this.isProfessionalTrader.value,
                termsAccepted: true as const,
            } satisfies z.infer<typeof CreateSubscriptionRequest>;
            if (TRACE) {
                this.#log.trace('Submitting subscription request:', request);
            }
            await this.#createSubscriptionMutation.mutate(request);
            if (TRACE) {
                this.#log.trace('Subscription response:', this.#state.value.createSubscriptionResult?.data);
                this.#log.trace('Client secret:', { clientSecret: this.clientSecret.value });
            }
            this.currentUserModel.notifyEntitlementsChanged();
        } catch (e: any) {
            const status = e?.response?.status;
            if (TRACE) {
                this.#log.trace('Subscription error:', e);
            }
            if (status === 400) {
                this.#dispatch({
                    type: 'SUBMIT_ERROR',
                    error: e?.response?.data?.message ?? 'Invalid subscription request.',
                });
            } else if (status === 404) {
                this.#dispatch({ type: 'SUBMIT_ERROR', error: 'Plan not found.' });
            } else {
                this.#dispatch({
                    type: 'SUBMIT_ERROR',
                    error: e instanceof Error ? e.message : 'Subscription setup failed. Please try again.',
                });
            }
            throw e;
        }
    }

    onPaymentConfirmed(): void {
        // Capture the current lastFailedAt so we can detect when the backend updates it to
        // signal a payment failure. Until it changes, pending_payment_method means the webhook
        // hasn't been processed yet and we should keep polling.
        const subscriptionId =
            this.#state.value.createSubscriptionResult?.data?.subscription.id ??
            this.#state.value.existingPendingSubscriptionId;
        const secPkg = this.#securityPackageQuery?.signal.value.data;
        let knownLastFailedAt: Instant | null = null;
        if (subscriptionId && secPkg) {
            const sub = secPkg.subscriptions.find((s) => s.id === subscriptionId);
            if (sub?.status === 'pending_payment_method') {
                knownLastFailedAt = sub.lastFailedAt ?? null;
            }
        }

        this.#dispatch({ type: 'PAYMENT_CONFIRMED', knownLastFailedAt });
        this.currentUserModel.notifyEntitlementsChanged();
    }

    goToStep(step: SubscriptionSetupStep): void {
        if (step === 'plan' && this.#state.value.currentStep === 'payment') {
            void this.cancelPendingSubscription();
        }
        this.#dispatch({ type: 'GO_TO_STEP', step });
    }

    cancelPendingSubscription(): Promise<void> {
        const subscriptionId =
            this.#state.value.createSubscriptionResult?.data?.subscription.id ??
            this.#state.value.existingPendingSubscriptionId;
        if (!subscriptionId) return Promise.resolve();

        if (TRACE) {
            this.#log.trace('Canceling pending subscription:', { subscriptionId });
        }

        // Reset local state upfront to avoid using stale clientSecret/subscriptionId
        // even if cancellation fails
        this.#createSubscriptionMutation.reset();
        this.#setupIntentMutation.reset();
        this.#dispatch({ type: 'CANCEL_PENDING_START' });

        return this.#cancelSubscriptionMutation
            .mutate({ id: subscriptionId, immediately: true })
            .then(() => {
                this.#dispatch({ type: 'CANCEL_PENDING_SUCCESS' });
                this.currentUserModel.notifyEntitlementsChanged();
            })
            .catch((e: unknown) => {
                if (TRACE) {
                    this.#log.trace('Failed to cancel pending subscription:', { error: e });
                }
                this.#dispatch({
                    type: 'CANCEL_PENDING_ERROR',
                    error: 'Failed to cancel previous subscription. Please try again or contact support.',
                });
            });
    }

    reset(): void {
        this.#createSubscriptionMutation.reset();
        this.#setupIntentMutation.reset();
        this.#planCatalog.resetInviteCode();

        // Invalidate cached subscription data so trial eligibility is re-evaluated with fresh data (i.e. when a trial subscription is canceled on period-end and that time period has passed).
        invalidateMySubscriptionsQuery(this.queryClient);

        const secPkg = this.#securityPackageQuery?.signal.value.data;
        const pendingSubscription = secPkg?.subscriptions.find((s) => s.status === 'pending_payment_method');

        if (pendingSubscription) {
            if (TRACE) {
                this.#log.trace('Found existing pending subscription, resuming:', {
                    subscriptionId: pendingSubscription.id,
                    planId: pendingSubscription.planId,
                });
            }
        }

        this.#dispatch({
            type: 'RESET',
            pendingSubscription: pendingSubscription
                ? { id: pendingSubscription.id, planId: pendingSubscription.planId }
                : null,
        });
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
